import * as  types from './action-types';
import { produce } from 'immer';
import { RenderMode } from 'app/types-shared';
import uniqBy from 'lodash/uniqBy';

import {
  CaseTableViewInstanceState, CaseTableViewState,
  PayloadCaseTableViewResultType
} from 'app/area/case-report/case-table-view/types-store';
import { ACTION } from './action-types';
import { EXECUTE_STATUS } from 'app/area/reports/containers/chart/types-store';

export const INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE: CaseTableViewInstanceState = {
  renderMode: RenderMode.report,
  fetching: false,
  executeStatus: EXECUTE_STATUS.IDLE,
  executeRequestId: 0,
  caseTableViewResults: undefined,
  errorsExecuteFailed: [],
  singleSupplierUrl: undefined
};

export const INITIAL_CASE_TABLE_VIEW_STATE: CaseTableViewState = {
  instances: {},
  texts: {} as any
};

export function CaseTableViewReducer(state: CaseTableViewState = INITIAL_CASE_TABLE_VIEW_STATE,
  action: types.CaseTableActionType): CaseTableViewState {
  switch (action.type) {
    case ACTION.REPLACE_CASE_VIEW_TABLE: {
      const { instanceKey, caseTableViewResults, requestId } = action.payload as PayloadCaseTableViewResultType;
      if (requestId !== undefined && requestId !== state.instances?.[instanceKey]?.executeRequestId) {
        return state;
      }
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...(state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE),
            fetching: false,
            executeStatus: EXECUTE_STATUS.SUCCEEDED,
            errorsExecuteFailed: [],
            renderMode: RenderMode.report,
            caseTableViewResults
          }
        }
      };
    }
    case ACTION.APPEND_CASE_VIEW_TABLE: {
      const { instanceKey, caseTableViewResults, requestId } = action.payload as PayloadCaseTableViewResultType;
      if (requestId !== undefined && requestId !== state.instances?.[instanceKey]?.executeRequestId) {
        return state;
      }
      const { entities, metadata } = caseTableViewResults || {};
      const currentInstance = state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE;

      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...currentInstance,
            caseTableViewResults: {
              metadata: {
                includedFields: uniqBy((currentInstance?.caseTableViewResults?.metadata?.includedFields || []).
                  concat((metadata?.includedFields || [])), 'columnName'),
                domains: {
                  ...(currentInstance?.caseTableViewResults?.metadata?.domains || {}),
                  ...(metadata?.domains || {})
                },
                totalNumberOfResults: metadata?.totalNumberOfResults
                  || currentInstance?.caseTableViewResults?.metadata?.totalNumberOfResults || 0
              },
              entities: (currentInstance?.caseTableViewResults?.entities || []).concat(entities || []),
              messages: currentInstance?.caseTableViewResults?.messages
            },
            fetching: false,
            executeStatus: EXECUTE_STATUS.SUCCEEDED
          }
        }
      };
    }
    case ACTION.REQUEST_CASE_VIEW_TABLE: {
      return produce(state, draftState => {
        if (!draftState.instances) {
          draftState.instances = {};
        }
        draftState.instances[action.payload?.instanceKey] = {
          ...(draftState.instances[action.payload?.instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE),
          errorsExecuteFailed: [],
          fetching: true,
          executeStatus: EXECUTE_STATUS.PENDING,
          executeRequestId: ((draftState.instances[action.payload?.instanceKey]?.executeRequestId) ?? 0) + 1
        };
        return draftState;
      });
    }
    case ACTION.CLEAR_CASE_VIEW_TABLE: {
      const { instanceKey } = action.payload as PayloadCaseTableViewResultType;
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE
          }
        }
      };
    }

    case ACTION.FAIL_EXECUTE_FAVOURITE_CASE_VIEW_TABLE: {
      const { instanceKey, errorsExecuteFailed, requestId } =
        action.payload as { instanceKey: string; errorsExecuteFailed: string[]; requestId?: number };
      if (requestId !== undefined && requestId !== state.instances?.[instanceKey]?.executeRequestId) {
        return state;
      }
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...(state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE),
            fetching: false,
            executeStatus: EXECUTE_STATUS.FAILED,
            errorsExecuteFailed
          }
        }
      };
    }

    case ACTION.REMOVE_ITEM_CASE_VIEW_TABLE: {
      const { instanceKey, caseId } = action.payload as { instanceKey: string; caseId: number };
      const currentInstance = state.instances?.[instanceKey];
      if (!currentInstance?.caseTableViewResults) {
        return state;
      }
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...currentInstance,
            caseTableViewResults: {
              ...currentInstance.caseTableViewResults,
              entities: currentInstance.caseTableViewResults.entities?.filter(e => e?.[0].primaryKey?.value !== caseId) || []
            }
          }
        }
      };
    }

    case ACTION.RECEIVE_SINGLE_SUPPLIER_URL_CASE_VIEW_TABLE: {
      const { instanceKey, singleSupplierUrl } = action.payload as { instanceKey: string; singleSupplierUrl: string };
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...(state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE),
            singleSupplierUrl: singleSupplierUrl
          }
        }
      };
    }

    case ACTION.REQUEST_LAYOUT_MODEL: {
      const { instanceKey } = action.payload as { instanceKey: string };
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...(state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE)
          }
        }
      };
    }

    case ACTION.RECEIVE_LAYOUT_MODEL: {
      const { instanceKey, layoutModel } = action.payload as { instanceKey: string; layoutModel: any };
      return {
        ...state,
        instances: {
          ...state.instances,
          [instanceKey]: {
            ...(state.instances?.[instanceKey] || INITIAL_CASE_TABLE_VIEW_INSTANCE_STATE),
            layoutModel
          }
        }
      };
    }

    default:
      return state;
  }
}
